import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequestScopes } from '@/lib/auth/msal-config';
import { ensureMsalInitialized, msalInstance } from '@/lib/auth/msalInstance';

/**
 * Wrapper fino de fetch para chamadas à API do Pipeline Vega.
 *
 * Responsabilidades:
 * - Acrescenta `Authorization: Bearer <token>` via MSAL silent token acquisition (com fallback
 *   interativo — achado F4, revisão 2026-07).
 * - Usa a instância MSAL COMPARTILHADA (mesma do MsalProvider), então enxerga a conta logada.
 * - Anexa `Content-Type: application/json` por padrão (override possível).
 * - Lança em status >= 400 com detalhe do problem+json (RFC 7807) quando presente.
 *
 * NÃO faz: cache, retry, deduplicação — isso fica no TanStack Query.
 *
 * TODO (futuro):
 * - Adicionar correlation id (header `x-correlation-id`) para troubleshooting cross-stack.
 */

async function acquireAccessToken(): Promise<string | null> {
  await ensureMsalInitialized();
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: apiRequestScopes,
      account: accounts[0],
    });
    return result.accessToken;
  } catch (err) {
    // Achado F4: sem este fallback, uma renovação silenciosa que precisa de interação (MFA step-up,
    // revogação, refresh token expirado) estourava erro cru em vez de redirecionar pro login — o
    // usuário ficava destravado só saindo e entrando manualmente. Redirect (não popup), por
    // consistência com o loginRedirect já usado no AuthGate.
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: apiRequestScopes, account: accounts[0] });
      return null; // acquireTokenRedirect navega a página; não deveria alcançar este retorno
    }
    throw err;
  }
}

/**
 * Base URL absoluta da API. Em produção vem do config.js de runtime (window.__APP_CONFIG__);
 * em dev local fica vazio e o proxy do Vite (vite.config.ts) encaminha /api -> localhost:7071.
 */
function apiBaseUrl(): string {
  return window.__APP_CONFIG__?.apiBaseUrl ?? '';
}

export interface ApiError extends Error {
  status: number;
  problemDetails?: Record<string, unknown>;
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Extrai a mensagem de validação mais útil de um erro da API (RFC-ish do ExceptionHandlingMiddleware:
 * `{ error, errors: [{ property, message }] }`). Útil para exibir 400s legíveis ao usuário (ex.: a
 * contagem da guarda de taxonomia em uso). Retorna undefined se não houver mensagem específica.
 */
export function firstValidationMessage(err: unknown): string | undefined {
  const pd = (err as ApiError)?.problemDetails;
  if (!pd) return undefined;
  const errors = pd.errors as Array<{ message?: string }> | undefined;
  if (Array.isArray(errors) && errors[0]?.message) return errors[0].message;
  if (typeof pd.error === 'string' && pd.error !== 'Validação falhou.') return pd.error;
  return undefined;
}

export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const token = await acquireAccessToken();

  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Content-Type') && init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${apiBaseUrl()}${path}`, {
    ...init,
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (!response.ok) {
    let problemDetails: Record<string, unknown> | undefined;
    try {
      problemDetails = (await response.json()) as Record<string, unknown>;
    } catch {
      // Sem corpo JSON — mantém problemDetails undefined.
    }
    const error = new Error(
      `API ${response.status} ${response.statusText} em ${path}`,
    ) as ApiError;
    error.status = response.status;
    error.problemDetails = problemDetails;
    throw error;
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return (await response.json()) as T;
}
